import Axios from 'axios'

import { fetchLogAll, fetchLogUser } from '@/api/services/log.service'

const state = {
  activityLogs: [],
  activityLog_detail: {},
  loading: false,
  error: null,
  host: process.env.VUE_APP_API_HOST || 'http://localhost:8020',
  pagination: {
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false
  },
  filters: {
    username: null,
    action: null,
    deviceOwner: null,
    startDate: null,
    endDate: null,
    deviceId: null
  }
}

const getters = {
  getAllActivityLogs: state => Array.isArray(state.activityLogs) ? state.activityLogs : [],
  getActivityLogs: state => Array.isArray(state.activityLogs) ? state.activityLogs : [], // Add alias for backward compatibility
  getActivityLogDetail: state => state.activityLog_detail,
  isLoading: state => state.loading,
  getError: state => state.error,
  getPagination: state => state.pagination,
  getCurrentPage: state => state.pagination.currentPage,
  getTotalElements: state => state.pagination.totalElements,
  getFilters: state => state.filters
}

const mutations = {
  SET_LOADING(state, status) {
    state.loading = status
  },
  SET_ACTIVITY_LOGS(state, activityLogs) {
    state.activityLogs = Array.isArray(activityLogs) ? activityLogs : []
  },
  SET_ACTIVITY_LOG_DETAIL(state, activityLog) {
    state.activityLog_detail = activityLog
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_PAGINATION(state, paginationData) {
    state.pagination = {
      currentPage: paginationData.number,
      totalPages: paginationData.totalPages,
      totalElements: paginationData.totalElements,
      pageSize: paginationData.size,
      hasNext: !paginationData.last,
      hasPrevious: !paginationData.first
    }
  },
  CLEAR_ERROR(state) {
    state.error = null
  },
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters }
  },
  CLEAR_FILTERS(state) {
    state.filters = {
      username: null,
      action: null,
      deviceOwner: null,
      startDate: null,
      endDate: null,
      deviceId: null
    }
  }
}

const actions = {
  async getActivityLogs({ commit, rootState }, { page = 0, size = 10, filters = {} } = {}) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      // Build query parameters
      const params = {
        page,
        size,
        includeDeviceInfo: true
      }
      
      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          // Special handling for date filters to ensure proper format
          if (key === 'startDate' || key === 'endDate') {
            // Convert to ISO format if needed
            const date = new Date(value)
            if (!isNaN(date.getTime())) {
              params[key] = date.toISOString().slice(0, 19) // Format: yyyy-MM-ddTHH:mm:ss
            }
          } else if (key === 'action') {
            // Map 'action' to 'activity' for backend compatibility
            params['activity'] = value
          } else if (key === 'username') {
            // Map 'username' to 'idUser' for backend compatibility
            params['idUser'] = value
          } else {
            params[key] = value
          }
        }
      })
      
      console.log('Sending request with params:', params)
      
      // const response = await Axios.get(`${state.host}/api/activity-log/search`, {
      //   params,
      //   headers: {
      //     'Authorization': `Bearer ${rootState.token}`
      //   }
      // })

      const response = await fetchLogAll(params);
      
      console.log('Raw API response:', response.data)
      
      // Handle different response structures
      let activityLogs = []
      let paginationData = null
      
      if (response.data.data && response.data.data.content) {
        // Structure: { data: { content: [...], totalElements: ... } }
        activityLogs = response.data.data.content || []
        paginationData = response.data.data
        console.log('Using structure 1: data.data.content')
      } else if (response.data.success && response.data.data) {
        // Structure: { success: true, data: { content: [...], totalElements: ... } }
        activityLogs = response.data.data.content || []
        paginationData = response.data.data
        console.log('Using structure 2: success + data.data.content')
      } else if (response.data.content) {
        // Structure: { content: [...], totalElements: ... }
        activityLogs = response.data.content
        paginationData = response.data
        console.log('Using structure 3: data.content')
      } else if (Array.isArray(response.data)) {
        // Structure: [...]
        activityLogs = response.data
        console.log('Using structure 4: direct array')
      }
      
      console.log('Extracted activityLogs:', activityLogs)
      
      if (Array.isArray(activityLogs)) {
        activityLogs = activityLogs.map(log => ({
          id: log.id,
          username: log.idUser || log.username,
          action: log.activity || log.action,
          description: log.details || log.description || `${log.activity || log.action} activity`,
          timestamp: log.timestamp || log.createdAt,
          ipAddress: log.ipAddress || 'Unknown',
          userAgent: log.userAgent || 'Unknown',
          status: log.status || 'SUCCESS',
          sessionId: log.sessionId,
          additionalData: log.additionalData,
          // Device information
          idDevice: log.idDevice,
          deviceName: log.deviceName,
          deviceType: log.deviceType || log.platform,
          deviceOwnerName: log.deviceOwnerName,
          platform: log.platform,
          durationMs: log.durationMs,
          errorMessage: log.errorMessage,
          ...log // Keep original data as well
        }))
        console.log('Mapped activityLogs:', activityLogs)
      } else {
        console.warn('activityLogs is not an array:', activityLogs)
      }
      
      commit('SET_ACTIVITY_LOGS', activityLogs)
      if (paginationData && (paginationData.pageable || paginationData.totalPages !== undefined)) {
        commit('SET_PAGINATION', paginationData)
      }
      
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Gagal memuat activity logs')
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async getUserActivityLogs({ commit, rootState }, { username, page = 0, size = 10 }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const params = { page, size }
      
      // const response = await Axios.get(`${state.host}/api/activity-log/user/${username}`, {
      //   params,
      //   headers: {
      //     'Authorization': `Bearer ${rootState.token}`
      //   }
      // })

      const response = await fetchLogUser({ username }, params);
      
      // Handle different response structures
      let activityLogs = []
      let paginationData = null
      
      if (response.data.data && response.data.data.content) {
        // Structure: { data: { content: [...], totalElements: ... } }
        activityLogs = response.data.data.content || []
        paginationData = response.data.data
      } else if (response.data.success && response.data.data) {
        // Structure: { success: true, data: { content: [...], totalElements: ... } }
        activityLogs = response.data.data.content || []
        paginationData = response.data.data
      } else if (response.data.content) {
        // Structure: { content: [...], totalElements: ... }
        activityLogs = response.data.content
        paginationData = response.data
      } else if (Array.isArray(response.data)) {
        // Structure: [...]
        activityLogs = response.data
      }
      
      if (Array.isArray(activityLogs)) {
        activityLogs = activityLogs.map(log => ({
          id: log.id,
          username: log.idUser || log.username,
          action: log.activity || log.action,
          description: log.details || log.description || `${log.activity || log.action} activity`,
          timestamp: log.timestamp || log.createdAt,
          ipAddress: log.ipAddress || 'Unknown',
          userAgent: log.userAgent || 'Unknown',
          status: log.status || 'SUCCESS',
          sessionId: log.sessionId,
          additionalData: log.additionalData,
          // Device information
          idDevice: log.idDevice,
          deviceName: log.deviceName,
          deviceType: log.deviceType || log.platform,
          deviceOwnerName: log.deviceOwnerName,
          platform: log.platform,
          durationMs: log.durationMs,
          errorMessage: log.errorMessage,
          ...log // Keep original data as well
        }))
      }
      
      commit('SET_ACTIVITY_LOGS', activityLogs)
      if (paginationData && (paginationData.pageable || paginationData.totalPages !== undefined)) {
        commit('SET_PAGINATION', paginationData)
      }
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Gagal memuat user activity logs')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async exportActivityLogs({ commit, rootState }, { format = 'excel', filters = {} }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      // Build query parameters
      const params = { format }
      
      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params[key] = value
        }
      })
      
      const response = await Axios.get(`${state.host}/api/activity-log/export`, {
        params,
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        },
        responseType: 'blob'
      })
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      
      // Set filename based on format
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
      const filename = `activity-log-${timestamp}.${format === 'excel' ? 'xlsx' : format}`
      link.setAttribute('download', filename)
      
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
      return { success: true, filename }
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Gagal mengekspor activity logs')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  setFilters({ commit }, filters) {
    commit('SET_FILTERS', filters)
  },

  clearFilters({ commit }) {
    commit('CLEAR_FILTERS')
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
